const React = require('react');
const { useFormState, useWatch } = require('react-hook-form');
const InvalidSubmitFocusUtils = require('hooks/useFormPrimitives/invalidSubmitFocusUtils');
const FieldContext = require('./FieldContext');

const { useMemo } = React;

const internals = {};

internals.getPath = (obj, path) => {

    if (!path) {
        return obj;
    }

    return String(path).split('.').reduce((current, key) => current?.[key], obj);
};

internals.hasFieldError = ({
    fieldName,
    fieldError,
    methods = {}
}) => !!fieldError
    || !!internals.getPath(methods.formState?.errors, fieldName)
    || !!methods.getFieldState?.(fieldName)?.error;

internals.buildReactHookFormTextFieldProps = ({
    definition = {},
    fieldError,
    textFieldProps = {}
}) => {

    const definitionTextFieldProps = definition.textFieldProps || {};
    const hasError = !!fieldError || !!textFieldProps.error || !!definitionTextFieldProps.error;

    return {
        variant: 'filled',
        ...definitionTextFieldProps,
        ...textFieldProps,
        error: hasError,
        helperText: fieldError?.message
            || textFieldProps.helperText
            || definitionTextFieldProps.helperText
            || null,
        inputProps: {
            ...(definitionTextFieldProps.inputProps || {}),
            ...(textFieldProps.inputProps || {}),
            'aria-invalid': hasError || undefined
        }
    };
};

exports.useReactHookFormField = ({
    field = {},
    fieldName,
    methods = {}
}) => {

    const control = methods.control;
    const value = useWatch({
        control,
        name: fieldName
    });
    const formState = useFormState({
        control
    });
    const fieldError = internals.getPath(formState.errors, fieldName)
        || methods.getFieldState?.(fieldName, formState)?.error;
    const definition = useMemo(() => ({
        ...field,
        name: fieldName
    }), [field, fieldName]);

    return useMemo(() => ({
        definition,
        formConfig: undefined,
        formMethods: methods,
        formProps: {},
        getValue: () => typeof value === 'undefined'
            ? methods.getValues?.(fieldName)
            : value,
        setValue: (nextValue, options = {}, event) => {
            methods.setValue?.(fieldName, nextValue, {
                shouldDirty: true,
                shouldValidate: false,
                ...options
            });

            if (typeof definition.onChange === 'function') {
                definition.onChange(nextValue, event);
            }
        },
        getError: () => fieldError?.message || null,
        getHelperText: () => fieldError?.message
            || definition.helperText
            || definition.textFieldProps?.helperText
            || null,
        getTextFieldProps: (textFieldProps = {}) => internals.buildReactHookFormTextFieldProps({
            definition,
            fieldError,
            textFieldProps
        }),
        hasError: () => internals.hasFieldError({
            fieldName,
            fieldError,
            methods
        }),
        onBlur: () => undefined,
        onFocus: (event) => {
            if (InvalidSubmitFocusUtils.shouldPreserveErrorOnFocus(event)) {
                return;
            }

            if (internals.hasFieldError({
                fieldName,
                fieldError,
                methods
            })) {
                methods.clearErrors?.(fieldName);
            }
        }
    }), [definition, fieldError, fieldName, methods, value]);
};

exports.useResolvedField = ({
    field = {},
    fieldName,
    methods,
    requestedUseFieldHook
}) => {

    const fieldContext = FieldContext.useFieldContext();

    if (typeof requestedUseFieldHook === 'function') {
        return requestedUseFieldHook(fieldName);
    }

    if (fieldContext) {
        return FieldContext.useField(fieldName);
    }

    return exports.useReactHookFormField({
        field,
        fieldName,
        methods
    });
};

exports.internals = internals;
